import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import React from 'react';

import ImagePlaceholder from '../../assets/images/image-placeholder.jpg';
import './CdnImage.scss';

export interface CdnImageProps {
  classes?: {
    loading?: string;
    image?: string;
    root?: string;
  };
  isLoading?: boolean;
  className?: string;
  rounded?: boolean;
  height?: number;
  width?: number;
  src?: string;
  alt?: string;
  // Pass 'eager' for above-the-fold hero/cover images (LCP candidates).
  loading?: 'eager' | 'lazy';
}

export const CdnImage = ({
  alt,
  className,
  classes,
  height,
  isLoading,
  loading = 'lazy',
  rounded = true,
  src,
  width,
}: CdnImageProps) => {
  const [isImageLoading, setIsImageLoading] = React.useState(
    isLoading ?? Boolean(src),
  );
  const [hasError, setHasError] = React.useState(false);

  const showSkeleton = Boolean(isLoading || (src && isImageLoading));

  React.useEffect(() => {
    setHasError(false);
    setIsImageLoading(isLoading ?? Boolean(src));
  }, [isLoading, src]);

  const getImageComponent = React.useCallback(
    (imageUrl?: string) => (
      <img
        onError={() => {
          setHasError(true);
          setIsImageLoading(false);
        }}
        className={classNames('Mfw-CdnImage-image', classes?.image)}
        onLoad={() => setIsImageLoading(false)}
        loading={loading}
        alt={alt ?? ''}
        src={imageUrl}
      />
    ),
    [classes?.image, alt, loading],
  );

  const styles = React.useMemo(
    () => ({
      height: height && height >= 0 ? height : '100%',
      width: width && width >= 0 ? width : '100%',
    }),
    [width, height],
  );

  return (
    <div
      className={classNames(
        'Mfw-CdnImage-root',
        {
          'Mfw-CdnImage-rounded': rounded,
        },
        className,
        classes?.root,
      )}
      style={styles}
    >
      {showSkeleton ? (
        <Skeleton
          className={classNames('Mfw-CdnImage-loading', classes?.loading)}
          variant="rectangular"
        />
      ) : null}
      {src && !hasError && getImageComponent(src)}
      {hasError ? getImageComponent(ImagePlaceholder) : null}
    </div>
  );
};

CdnImage.displayName = 'CdnImage';

export default CdnImage;
